import { FunctionComponent, useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import { useIntl } from 'react-intl'
import { NavigationMenuProps } from './types'
import { useBreakpoints } from '../../../hooks/useBreakpoints'
import DesktopNavigationMenu from './desktop/desktop-navigation-menu'
import MobileNavigationMenu from './mobile/mobile-navigation-menu'
import { useLocale } from '../../../hooks/useLocale'
import { Link } from 'gatsby'

const NavigationMenu: FunctionComponent<NavigationMenuProps> = ({ logo, ctaButton, items }) => {
  if (!logo || !items) return
  const { locale } = useLocale()
  const intl = useIntl()
  const { isDesktopOrBigger } = useBreakpoints()

  // Skip navigation button logic
  const skipNavRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    const handleSkipNavigation = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && skipNavRef.current) {
        const mainElement = document.querySelector<HTMLElement>('main')

        if (mainElement) {
          const focusableElements = mainElement.querySelectorAll<HTMLElement>(
            'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
          )

          const firstFocusableElement = Array.from(focusableElements).find(
            element => !element.hasAttribute('disabled') && element.getAttribute('tabindex') !== '-1'
          )

          if (firstFocusableElement) {
            firstFocusableElement.focus()
            event.preventDefault()
          } else {
            // If no focusable elements are found inside main, set focus to main itself
            mainElement.setAttribute('tabindex', '-1')
            mainElement.focus()
            event.preventDefault()
          }
        }
      }
    }

    const skipNavLink = skipNavRef.current
    if (skipNavLink) {
      skipNavLink.addEventListener('keydown', handleSkipNavigation)
    }

    return () => {
      if (skipNavLink) {
        skipNavLink.removeEventListener('keydown', handleSkipNavigation)
      }
    }
  }, [])
  // /////////////////

  return (
    <Navigation role="navigation">
      <SkipNavigation href="#" ref={skipNavRef} onClick={e => e.preventDefault()}>
        Skip to Content
      </SkipNavigation>
      <Link to={`/${locale}/`} title={intl.formatMessage({ id: 'link.backToHomepage' })}>
        {!logo._isFallback && <Logo src={logo.url} alt={logo.alternativeText ?? ''} />}
      </Link>
      {isDesktopOrBigger && <DesktopNavigationMenu items={items} ctaButton={ctaButton} />}
      {!isDesktopOrBigger && <MobileNavigationMenu items={items} ctaButton={ctaButton} />}
    </Navigation>
  )
}

const Navigation = styled.nav`
  z-index: var(--zIndex-dropdown);
  width: 100%;
  min-height: var(--navigation-height);
  padding: 1.25rem var(--spacing-xxxl);
  position: fixed;

  display: flex;
  align-items: center;
  justify-content: space-between;

  background-color: var(--dark-background-color);
  border-bottom: 1px solid var(--beige-300, #cecabd);

  @media (max-width: 1280px) {
    padding: 0.625rem 1rem;
  }
`

const Logo = styled.img`
  display: block;
  max-height: 3.1875rem; /* 51px */
`

const SkipNavigation = styled.a`
  position: absolute;
  width: 0px;
  overflow: hidden;
  z-index: 0;

  &:focus {
    position: relative;
    width: auto;
    background: var(--neutral-950);
    color: var(--light-text-color);
    padding: 0.5rem;
    z-index: var(--zIndex-modal);
  }
`

export * from './types'
export default NavigationMenu
